import classnames from 'classnames'
import React, { useContext, useEffect, useId, useMemo, useRef } from 'react'

import styles from './Modal.module.scss'

type ModalProps = React.DialogHTMLAttributes<HTMLDialogElement>

const modalContext = React.createContext({ id: undefined as string | undefined })

export const useModalContext = () => useContext(modalContext)

export default React.memo(function Modal({ children, className, ...dialogProps }: ModalProps) {
  const dialogId = useId()
  const modalContextValue = useMemo(() => ({ id: dialogId }), [dialogId])

  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    if (dialogRef.current) {
      const scrollY = window.scrollY
      const body = document.body

      const previousPosition = body.style.position
      const previousTop = body.style.top
      const previousWidth = body.style.width
      const currentWidth = body.clientWidth

      body.style.position = 'fixed'
      body.style.top = `-${scrollY}px`
      body.style.width = `${currentWidth}px`

      dialogRef.current.showModal()
      return () => {
        body.style.position = previousPosition
        body.style.top = previousTop
        body.style.width = previousWidth
        window.scrollTo(0, scrollY)
      }
    }
  }, [dialogRef.current])

  return (
    <dialog id={dialogId} className={classnames(styles.Modal, className)} ref={dialogRef} {...dialogProps}>
      <modalContext.Provider value={modalContextValue}>{children}</modalContext.Provider>
    </dialog>
  )
})
